/**
 * The one-touch milestone triggers — the decisions, with nothing around them.
 *
 * ── WHAT THIS FILE IS ───────────────────────────────────────────────────────
 *
 * Every rule from `claude/milestone-trigger-logic.md`, as plain functions over
 * plain values. No database, no network, no Metabase, no clock. `today` is
 * always a parameter, never `new Date()`, because a trigger that reads the
 * clock cannot be asked what it does on 29 February.
 *
 * It deliberately does NOT decide where the daily read runs. That question is
 * open (extension alarm, app-on-first-load, some cron), and it is the one thing
 * that would have blocked building any of this — so it is kept out. Whatever
 * ends up calling this hands it the day's rows and gets back a plan.
 *
 * ── WHAT THE CALLER OWES THIS FUNCTION ──────────────────────────────────────
 *
 * Two things, and both are easy to get wrong in a way that produces silence
 * rather than an error.
 *
 * **1. `agents` must be the roster as it stood BEFORE today's Metabase values
 * were written back into it.** The three full-list cards fire on the card
 * disagreeing with the record. Hand this a roster that has already been
 * updated from the same feed and every comparison matches, so capping, Elite
 * and first closing never fire again — no draft, and no error to notice it by.
 * Read first, plan, then write.
 *
 * **2. `logged` must be every `outreach_log` row for the agents in play**, not
 * a recent window. It is the dedupe ledger and the authority on what has
 * already been said; a truncated one re-sends things.
 *
 * What the caller does NOT owe: keeping `agents.date_became_capped` and friends
 * in step. Those columns are consulted only as a fallback — the log is checked
 * first — so a caller that never backfills them still behaves correctly.
 *
 * ── WHY A PLAN RATHER THAN WRITES ───────────────────────────────────────────
 *
 * `plan()` returns what SHOULD happen — drafts to create, log rows to write,
 * enrolments to start, and for everything it declined, the reason. It writes
 * nothing. That is what makes the whole thing assertable in a project with no
 * browser harness: the interesting failures here are all decisions ("this
 * birthday fired a month early", "this agent got ninety welcome emails"), and
 * a decision that returns a value can be checked. A decision that performs a
 * write can only be checked by performing it.
 *
 * ── THE ONE SENTENCE WORTH KEEPING IN MIND ──────────────────────────────────
 *
 * Five of the seven triggers fire on what the day's feed says. **Birthdays and
 * anniversaries do not** — the feed fills the date, and the calendar fires the
 * email. That distinction was got wrong twice before it was written down; it is
 * the reason `annualDue` exists and takes no feed rows at all.
 */

/**
 * The seven, named exactly as `email_templates.key` and `outreach_log.trigger`
 * already name them.
 *
 * `welcome`, not `joined` — the template key for a new joiner is `welcome`, and
 * inventing a parallel name for the same event is how two halves of a system
 * come to disagree about whether somebody has been written to.
 */
export type Trigger =
  | 'birthday'
  | 'anniversary'
  | 'capped'
  | 'elite'
  | 'first_closing'
  | 'welcome'
  | 'departed'
  /**
   * Added 2026-09-02. An agent who appears in the full South Carolina active
   * roster without ever having appeared in New Joiners — already with Real,
   * newly active/licensed in THIS state. Deliberately its own trigger, not a
   * variant of `welcome`: a `welcome` email says "welcome to Real", which is
   * wrong for somebody who has been there for months. See
   * `claude/sc-licensed-trigger-build-2026-09-02.md`.
   */
  | 'sc_licensed'

/**
 * Which of the three detection shapes each trigger uses. Stated as data rather
 * than as `if` branches, because the shape is the part that turns one email
 * into ninety and it deserves to be readable in one place.
 */
export const SHAPE: Record<Trigger, 'annual' | 'appearance' | 'new_date'> = {
  birthday: 'annual',
  anniversary: 'annual',
  welcome: 'appearance',
  departed: 'appearance',
  sc_licensed: 'appearance',
  capped: 'new_date',
  elite: 'new_date',
  first_closing: 'new_date',
}

/** How the copy should read. Only the annual triggers have variants. */
export type Variant = 'on_day' | 'early' | 'belated'

// ───────────────────────────────────────────────────────── dates, as strings

/**
 * Every date in this file is a `YYYY-MM-DD` string and is manipulated as one.
 *
 * `new Date('2026-09-12')` is midnight UTC, which is the evening of the 11th in
 * New York — the exact bug `format.ts` documents having shipped once already,
 * on every date-only column in the database. A birthday engine that made it
 * would send Renee's cards a day early for everyone west of Greenwich, all
 * year, and the only symptom would be agents mentioning it in replies.
 *
 * So: no `Date` parsing of date-only strings anywhere below. Arithmetic is done
 * on UTC epoch days, which is safe precisely because both ends are UTC and the
 * result is converted straight back to a string.
 */
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/

export function isDateString(v: unknown): v is string {
  if (typeof v !== 'string') return false
  const m = DATE_RE.exec(v)
  if (!m) return false
  const [, y, mo, d] = m
  const month = Number(mo)
  const day = Number(d)
  if (month < 1 || month > 12) return false
  return day >= 1 && day <= daysInMonth(Number(y), month)
}

export const isLeapYear = (y: number): boolean =>
  (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0

export function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28
  return [4, 6, 9, 11].includes(month) ? 30 : 31
}

const iso = (y: number, m: number, d: number) =>
  `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`

/**
 * The calendar day inside a value that may be a date or a full timestamp.
 *
 * `first_transaction_closed_at` is a timestamp; `date_became_capped` is a date.
 * Both arrive here and both have to compare equal to the same day, or a card
 * saying `2026-08-01` and a record holding `2026-08-01T14:00:00Z` would look
 * like a change every single day and draft a "first closing" email every day
 * forever.
 *
 * It takes the leading ten characters rather than parsing, for the reason
 * above: parsing would move the day for anybody not on UTC, which is everybody
 * here.
 */
export function dayOf(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null
  const head = value.slice(0, 10)
  return isDateString(head) ? head : null
}

/** Days since 1970-01-01, from a date string. Pure arithmetic, no `Date`. */
export function epochDay(date: string): number {
  const m = DATE_RE.exec(date)
  if (!m) throw new Error(`not a date: ${date}`)
  return Math.floor(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / 86_400_000)
}

export function fromEpochDay(days: number): string {
  const d = new Date(days * 86_400_000)
  return iso(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate())
}

export const addDays = (date: string, n: number): string => fromEpochDay(epochDay(date) + n)

/** How many days from `a` to `b`. Negative when `b` is in the past. */
export const daysBetween = (a: string, b: string): number => epochDay(b) - epochDay(a)

/** 0 = Sunday … 6 = Saturday. 1970-01-01 was a Thursday, which is where the 4 comes from. */
export function weekdayOf(date: string): number {
  return (((epochDay(date) + 4) % 7) + 7) % 7
}

export const isWeekend = (date: string): boolean => {
  const w = weekdayOf(date)
  return w === 0 || w === 6
}

// ────────────────────────────────────────────── birthdays and anniversaries

/**
 * How far back a date that has already passed may still be worth acknowledging.
 *
 * Sixty days, because that is the width of the Metabase look-back card — a date
 * older than that cannot reach us anyway, so a wider window would only ever
 * fire on data we already had and deliberately let go. Chris, 2026-08-19:
 * *"maintain belated."* A late birthday card beats silence; a birthday card two
 * months late does not.
 */
export const BELATED_WINDOW_DAYS = 60

/**
 * A tighter belated window for an agent who has barely been in our roster.
 *
 * **The bug this guards, found 2026-08-28:** a brand-new agent can land on
 * the birthday card with a real birth date anywhere in the last sixty days —
 * nothing about the birthday card itself is new-agent-aware. Combined with a
 * join date that is, by definition, recent, the ordinary sixty-day belated
 * window was willing to draft a "happy belated birthday" for someone who
 * joined the roster today and whose birthday was, in one observed case,
 * thirty-one days ago. Confirmed live: Mary Hannah Silvia, joined 2026-08-28.
 *
 * This does not touch `BELATED_WINDOW_DAYS` itself — an established agent
 * whose birthday card was missed for a few weeks still gets the full
 * sixty-day courtesy (Chris, 2026-08-19: *"maintain belated"*). It only
 * narrows the window for an agent whose `first_seen_at` is itself inside this
 * many days of `today` — so a birthday from a day or two before someone
 * joined still fires belated, exactly as the 2026-08-20 standing decision
 * describes, while a birthday from a month before a brand-new join date does
 * not.
 *
 * Deliberately the same 7 as the window itself: the question is not "how new
 * counts as new" on its own scale, it is "would this belated date have been
 * inside the window if we only trusted the days since we met them" — so one
 * number answers both.
 */
export const NEW_AGENT_BELATED_WINDOW_DAYS = 7

/**
 * The date a month/day pair falls on in a given year.
 *
 * **29 February resolves to 28 February in a common year, and this is a
 * decision rather than an accident.** The alternative — 1 March — sends the
 * card the day *after* a birthday that did not happen, which reads as late.
 * Early reads as thoughtful, late reads as forgotten, and that is the whole
 * argument. Three of 785 agents will care; all of them will notice.
 */
export function occurrenceOn(year: number, month: number, day: number): string | null {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  const max = daysInMonth(year, month)
  // Only 29 February is clamped. A day of 31 in a 30-day month is bad data, not
  // a leap year, and quietly moving it would hide that.
  if (day > max) {
    if (month === 2 && day === 29) return iso(year, 2, 28)
    return null
  }
  const out = iso(year, month, day)
  /**
   * Checked against the same validator every other date goes through.
   *
   * **This cannot fire while `daysInMonth` is correct, and it is kept anyway.**
   * Said plainly so nobody writes a test for it and wonders why the test cannot
   * be made to fail: the thing that actually guards this is the month-length
   * assertion in `test-milestones.mjs`, which checks all twelve. What this
   * catches is the day `daysInMonth` is edited — an occurrence string is half
   * the key that stops an agent being emailed twice, and a value like
   * `2026-11-31` would be accepted, stored, and then silently rolled to
   * 1 December by any arithmetic that touched it.
   */
  return isDateString(out) ? out : null
}

/**
 * The day an occurrence should be surfaced on — the Friday before, when it
 * falls at the weekend.
 *
 * Chris, reconfirmed 2026-08-19: a Saturday date surfaces on the Friday before;
 * a Sunday date surfaces on the Friday before that. Both land on the Friday
 * immediately preceding, which is why one rule covers both.
 */
export function dueDate(occurrence: string): string {
  const w = weekdayOf(occurrence)
  if (w === 6) return addDays(occurrence, -1) // Saturday → Friday
  if (w === 0) return addDays(occurrence, -2) // Sunday → Friday
  return occurrence
}

export type Annual = {
  /** The dated year — `2026-09-12`. One draft per person per year, whatever the feed does. */
  occurrence: string
  variant: Variant
  /** The day this became due. Equal to `occurrence` except at the weekend. */
  dueOn: string
  /** Negative when the date has passed. 0 on the day. */
  daysAway: number
}

/**
 * Is this month/day due as of `today`, and how should it read?
 *
 * **This function never sees a feed row.** That is the whole point of the
 * exception: once 12 September is in the record, the birthday fires on 12
 * September whether or not Metabase was read that morning. A first-appearance
 * trigger fails on a day the sync does not run; this one does not care.
 *
 * Three candidate years are considered — last, this and next — because on 2
 * January a birthday of 30 December belongs to *last* year's occurrence, and on
 * 30 December a birthday of 1 January belongs to next year's. Picking the
 * candidate with the latest due date that is not in the future gets both right
 * without a special case for the turn of the year.
 */
export function annualDue(
  month: number | null | undefined,
  day: number | null | undefined,
  today: string,
  window: number = BELATED_WINDOW_DAYS,
): Annual | null {
  if (month == null || day == null) return null
  if (!isDateString(today)) return null

  const year = Number(today.slice(0, 4))
  let best: Annual | null = null

  for (const y of [year - 1, year, year + 1]) {
    const occurrence = occurrenceOn(y, month, day)
    if (!occurrence) continue
    const dueOn = dueDate(occurrence)
    // Not due yet. The next candidate year is further away still, so nothing
    // is lost by continuing rather than breaking.
    if (daysBetween(today, dueOn) > 0) continue
    const daysAway = daysBetween(today, occurrence)
    // Too old to be worth saying anything about — see BELATED_WINDOW_DAYS.
    if (-daysAway > window) continue
    const variant: Variant = daysAway === 0 ? 'on_day' : daysAway > 0 ? 'early' : 'belated'
    if (!best || epochDay(dueOn) > epochDay(best.dueOn)) {
      best = { occurrence, variant, dueOn, daysAway }
    }
  }

  return best
}

// ──────────────────────────────────────────────────────────── suppression

/**
 * The per-trigger tag that stops one kind of milestone email.
 *
 * **`first_closing` is tagged `no-firstTransaction-email`, and that is not a
 * typo.** It is the tag the old dashboard already applies, listed verbatim in
 * `SYSTEM_TAGS`. The obvious guess — `no-first-closing-email` — would compile,
 * pass a hand-written test, and silently email every agent who had asked not to
 * be. The lookup below normalises to lower case, so the stored camelCase and
 * the normalised form both match.
 *
 * **`departed` has no tag of its own.** `no-outreach` still stops it, which is
 * the decision from 2026-08-16: a farewell survey is still an email.
 */
export const TRIGGER_TAG: Record<Trigger, string | null> = {
  birthday: 'no-birthday-email',
  anniversary: 'no-anniversary-email',
  capped: 'no-capped-email',
  elite: 'no-elite-email',
  welcome: 'no-joined-email',
  first_closing: 'no-firstTransaction-email',
  departed: null,
  'sc_licensed': 'no-sc-licensed-email',
}

/** `no-outreach` covers the reviewed lane entirely — every milestone, including departures. */
export const ALL_MILESTONES_TAG = 'no-outreach'

/**
 * Which tag is stopping this trigger for this agent, or null if none is.
 *
 * Both sides are normalised. Tags predating the tag box, or typed into the
 * Supabase table editor, can be stored any way at all — `tagging.ts` says so
 * explicitly — and an exact-match lookup would let `No-Outreach` through as an
 * ordinary label while the agent believed they had opted out.
 */
export function suppressedFor(trigger: Trigger, tags: readonly string[]): string | null {
  const held = new Set(tags.map((t) => String(t).trim().toLowerCase()))
  if (held.has(ALL_MILESTONES_TAG)) return ALL_MILESTONES_TAG
  const own = TRIGGER_TAG[trigger]
  if (own && held.has(own.toLowerCase())) return own
  return null
}

// ────────────────────────────────────────────────────────────── templates

/**
 * The template keys to try, best first.
 *
 * **This returns candidates rather than a key, and that is deliberate.** The
 * agreed copy set is about fourteen pieces; the table currently holds six, and
 * `claude/milestone-trigger-logic.md` records that gap as real and open. A
 * function that returned `birthday_belated` today would produce drafts with no
 * body until somebody wrote it.
 *
 * So the specific variant is preferred and the base key is the fallback, which
 * means:
 *
 *   - today, a belated birthday drafts from `birthday` — slightly wrong wording,
 *     an email that goes out;
 *   - the day Renee adds a `birthday_belated` template, it starts being used,
 *     with no deploy and nobody remembering to come back here.
 *
 * Guidance keys (`_guidance_belated` and friends) are never candidates. They are
 * notes for whoever writes the copy, and `types.ts` is explicit that they must
 * never reach a send path.
 */
export function templateCandidates(trigger: Trigger | string, variant: Variant | null): string[] {
  const out: string[] = []
  if (variant && variant !== 'on_day' && takesVariants(trigger)) out.push(`${trigger}_${variant}`)
  out.push(trigger)
  return out
}

/**
 * The anniversary chain, with the low-production chain tried first (when it
 * applies) and the "with years" template tried before whichever plain chain
 * is in play, whenever the year is on file.
 *
 * Deliberately not a variant (2026-08-29, Chris: "Simple: one new template").
 * An agent whose year is known still falls back to the plain
 * `anniversary`/early/belated chain the normal way if nobody has written the
 * "with years" copy yet — `pickTemplate` just never finds `anniversary_years`
 * in `available` and moves on. Not offered to `COMBINED_KEY`: the merged
 * birthday+anniversary email stays out of scope for this.
 *
 * **The low-production chain (2026-09-20) works the identical way, one level
 * up.** Chris: keep the standard anniversary email exactly as it is for
 * anyone at or above the threshold; give anyone below it (or with no count on
 * file) a separate chain, offered first. Until that copy exists, `pickTemplate`
 * never finds any `anniversary_low_production*` key in `available` and falls
 * straight through to the standard chain above — so nothing changes for
 * anybody until the low-production templates are actually written, same
 * fail-safe shape as `ANNIVERSARY_YEARS_KEY`. See `isLowProduction` and
 * `lowProductionAnniversaryCandidates`.
 */
function anniversaryCandidates(agent: MilestoneAgent, variant: Variant | null): string[] {
  const base = templateCandidates('anniversary', variant)
  const standard = agent.anniversary_year != null ? [ANNIVERSARY_YEARS_KEY, ...base] : base
  if (!isLowProduction(agent)) return standard
  return [...lowProductionAnniversaryCandidates(agent, variant), ...standard]
}

/**
 * The most lifetime closed transactions an agent can have and still count as
 * "low production." Chris, 2026-09-20: "2 or fewer, so really the threshold
 * is below 3" — so 2 is low production, 3 is not.
 */
export const LOW_PRODUCTION_MAX_CLOSED_TRANSACTIONS = 2

/**
 * Below the line, or the count simply is not on file. See the doc comment on
 * `MilestoneAgent.closed_transactions` for why missing counts as low rather
 * than defaulting to the standard copy.
 */
function isLowProduction(agent: MilestoneAgent): boolean {
  const n = agent.closed_transactions
  return n == null || n <= LOW_PRODUCTION_MAX_CLOSED_TRANSACTIONS
}

/**
 * The low-production half of the anniversary chain — the same on_day/early/
 * belated and "with years" shape as `anniversaryCandidates` builds for the
 * standard chain, under the `anniversary_low_production` prefix instead of
 * `anniversary`.
 *
 * Built by hand rather than through `templateCandidates`, because
 * `anniversary_low_production` is not a registered `Trigger` — same reason
 * `ANNIVERSARY_YEARS_KEY` is kept out of `SHAPE` and `Trigger`, see its own
 * comment — and `takesVariants` would refuse a bare string a variant suffix.
 */
function lowProductionAnniversaryCandidates(agent: MilestoneAgent, variant: Variant | null): string[] {
  const out: string[] = []
  if (variant && variant !== 'on_day') out.push(`${ANNIVERSARY_LOW_PRODUCTION_KEY}_${variant}`)
  out.push(ANNIVERSARY_LOW_PRODUCTION_KEY)
  if (agent.anniversary_year != null) out.unshift(ANNIVERSARY_LOW_PRODUCTION_YEARS_KEY)
  return out
}

/**
 * Can this trigger's copy ever need an early or belated wording?
 *
 * Only the date-driven ones and the merged pair. Capping, Elite, a first
 * closing, joining and leaving are reported by the feed when they happen —
 * there is no "three days early" version of reaching cap.
 *
 * **Without this the function would cheerfully ask for `capped_early`**, which
 * nothing offers to create and nothing can ever answer, so it would fall
 * through to `capped` and look fine. Harmless today because `consider` passes
 * `null` for those triggers — and that is exactly the kind of "harmless
 * because of how it happens to be called" that stops being true when somebody
 * calls it differently. Caught by a test that asked for every trigger and
 * variant rather than only the combinations in use.
 */
function takesVariants(trigger: Trigger | string): boolean {
  if (trigger === COMBINED_KEY) return true
  return SHAPE[trigger as Trigger] === 'annual'
}

/**
 * The first candidate that actually exists, or null.
 *
 * Null is a real answer and the caller must handle it: there is no `departed`
 * template yet, and drafting from a template that does not exist would put an
 * empty email in front of Renee with no explanation of why it was blank.
 */
export function pickTemplate(candidates: readonly string[], available: readonly string[]): string | null {
  const have = new Set(available.filter((k) => !k.startsWith('_')))
  return candidates.find((k) => have.has(k)) ?? null
}

/**
 * The template for an agent whose birthday and work anniversary fall on the
 * same day.
 *
 * One person, one day, two milestones — and without this, two emails. The
 * combined key is tried first and the pair falls back to two separate drafts
 * when it does not exist, which is what happens today.
 */
export const COMBINED_KEY = 'birthday_anniversary'

/**
 * The "with years" anniversary template — same trigger and log key as
 * `anniversary`, just a different copy choice. See `anniversaryCandidates`.
 * Kept out of `SHAPE`/`Trigger` on purpose: it is not a new kind of event,
 * just a wording `anniversary` may use when the year is known.
 */
export const ANNIVERSARY_YEARS_KEY = 'anniversary_years'

/**
 * The low-production anniversary template — same trigger and log key as
 * `anniversary`, a different copy choice for an agent at or under
 * `LOW_PRODUCTION_MAX_CLOSED_TRANSACTIONS` lifetime closed transactions (or
 * with none on file). See `anniversaryCandidates` and `isLowProduction`.
 * Kept out of `SHAPE`/`Trigger` on purpose, same reasoning as
 * `ANNIVERSARY_YEARS_KEY`: not a new kind of event, just a wording
 * `anniversary` may use.
 */
export const ANNIVERSARY_LOW_PRODUCTION_KEY = 'anniversary_low_production'

/**
 * The low-production "with years" anniversary template — the low-production
 * counterpart to `ANNIVERSARY_YEARS_KEY`, tried first of all when both apply.
 * See `lowProductionAnniversaryCandidates`.
 */
export const ANNIVERSARY_LOW_PRODUCTION_YEARS_KEY = 'anniversary_low_production_years'

/**
 * Every template this engine will ever look for, and what to call it.
 *
 * **DERIVED FROM `SHAPE`, never typed out.** Add a trigger and its templates
 * appear here; the alternative is a hand-kept list that agrees with the engine
 * on the day it is written and quietly stops agreeing afterwards.
 *
 * This exists because of a trap found walking the Templates screen on
 * 2026-08-20. The Create box derives a key from the typed name by replacing
 * spaces with HYPHENS, and every key below uses UNDERSCORES. So "Birthday
 * belated" produced `birthday-belated`, the engine looked for
 * `birthday_belated`, found nothing, and silently fell back to the plain
 * birthday copy — a belated email worded as though it were the day itself,
 * with the template she wrote sitting on screen looking live. The key is
 * deliberately never shown, so there was no way to see it.
 *
 * The screen now offers these by name and creates them with the exact key, so
 * nobody types one.
 */
export type MilestoneTemplate = {
  key: string
  /** What it is called on screen. Matches the six already seeded. */
  name: string
  /** One line on when it is used, for whoever writes the copy. */
  when: string
}

/**
 * The name each trigger's template carries. Not `LABEL` — that is a sentence
 * fragment. Exported for the Triggers tab's reference list
 * (claude/milestone-triggers-tab-flagged-2026-08-23.md) — it surfaces this
 * rather than inventing new copy, same as `MILESTONE_TEMPLATES` already
 * does for the Templates screen.
 */
export const TEMPLATE_NAME: Record<Trigger, string> = {
  birthday: 'Birthday',
  anniversary: 'Work anniversary',
  capped: 'Cap reached',
  elite: 'Elite reached',
  first_closing: 'First closing',
  welcome: 'Welcome',
  departed: 'Departed',
  sc_licensed: 'Newly SC-licensed',
}

const COMBINED_NAME = 'Birthday and work anniversary'

/**
 * When each base template is used, in the words of somebody writing the
 * copy. Exported for the same Triggers-tab reference list `TEMPLATE_NAME` is.
 */
export const WHEN: Record<Trigger, string> = {
  birthday: 'A birthday, on the day itself.',
  anniversary: 'A work anniversary, on the day itself.',
  capped: 'An agent reaches cap.',
  elite: 'An agent reaches Elite.',
  first_closing: "An agent's first closed transaction.",
  welcome: 'A new agent joins. This one starts the welcome sequence rather than sending alone.',
  departed: 'An agent leaves. A farewell, sent for review like any other.',
  sc_licensed:
    'An agent appears in the full South Carolina roster without ever appearing in New ' +
    'Joiners — already with Real, newly active or licensed in this state. Not a welcome; ' +
    "they aren't new to the company.",
}

/**
 * The two variants that need their own copy. `on_day` is the base template, so
 * it is not in this list — `templateCandidates` never asks for `birthday_on_day`.
 */
const COPY_VARIANTS: readonly Exclude<Variant, 'on_day'>[] = ['early', 'belated']

const VARIANT_SUFFIX: Record<Exclude<Variant, 'on_day'>, string> = {
  early: ' — early',
  belated: ' — belated',
}

const VARIANT_WHEN: Record<Exclude<Variant, 'on_day'>, string> = {
  early: 'The date falls at the weekend, so it surfaces on the Friday before. Reads as early, not late.',
  belated: 'The date arrived after the day had passed. Acknowledging it beats saying nothing.',
}

function buildMilestoneTemplates(): MilestoneTemplate[] {
  const out: MilestoneTemplate[] = []
  for (const trigger of Object.keys(SHAPE) as Trigger[]) {
    out.push({ key: trigger, name: TEMPLATE_NAME[trigger], when: WHEN[trigger] })
    /** Only the annual triggers surface early or late, so only they need variants. */
    if (SHAPE[trigger] !== 'annual') continue
    for (const v of COPY_VARIANTS) {
      out.push({
        key: `${trigger}_${v}`,
        name: TEMPLATE_NAME[trigger] + VARIANT_SUFFIX[v],
        when: VARIANT_WHEN[v],
      })
    }
  }
  /**
   * The "with years" anniversary template. Not a trigger, not in `SHAPE`, and
   * deliberately has no early/belated variants of its own — an early or
   * belated "with years" send still asks for `anniversary_early`/
   * `anniversary_belated` first (see `anniversaryCandidates`), same as it
   * always did; only the on-time case gets a "with years" option at all.
   */
  out.push({
    key: ANNIVERSARY_YEARS_KEY,
    name: TEMPLATE_NAME.anniversary + ' — with years',
    when: 'A work anniversary, on the day itself, for an agent whose join year is on file.',
  })
  /**
   * The low-production anniversary templates (2026-09-20). Same shape as the
   * standard chain just above — a base, an early, a belated, and a "with
   * years" that covers all three timings — for an agent at or under
   * `LOW_PRODUCTION_MAX_CLOSED_TRANSACTIONS` lifetime closed transactions, or
   * with none on file. Not a trigger, not in `SHAPE`, same reasoning as
   * `ANNIVERSARY_YEARS_KEY` above.
   */
  const LOW_PRODUCTION_WHEN =
    `A work anniversary, for an agent with ${LOW_PRODUCTION_MAX_CLOSED_TRANSACTIONS} or fewer ` +
    'lifetime closed transactions (or none on file).'
  out.push({
    key: ANNIVERSARY_LOW_PRODUCTION_KEY,
    name: TEMPLATE_NAME.anniversary + ' — low production',
    when: `${LOW_PRODUCTION_WHEN} On the day itself.`,
  })
  for (const v of COPY_VARIANTS) {
    out.push({
      key: `${ANNIVERSARY_LOW_PRODUCTION_KEY}_${v}`,
      name: TEMPLATE_NAME.anniversary + ' — low production' + VARIANT_SUFFIX[v],
      when: VARIANT_WHEN[v],
    })
  }
  out.push({
    key: ANNIVERSARY_LOW_PRODUCTION_YEARS_KEY,
    name: TEMPLATE_NAME.anniversary + ' — low production — with years',
    when: `${LOW_PRODUCTION_WHEN} Join year is on file.`,
  })
  /**
   * The merged pair, which is not a trigger and so is not in `SHAPE`. It gets
   * variants too: `consider` passes the birthday's variant through to
   * `templateCandidates(COMBINED_KEY, …)`, so a belated pair really does ask
   * for `birthday_anniversary_belated` before falling back.
   */
  out.push({
    key: COMBINED_KEY,
    name: COMBINED_NAME,
    when: 'A birthday and a work anniversary on the same day. One email, not two.',
  })
  for (const v of COPY_VARIANTS) {
    out.push({
      key: `${COMBINED_KEY}_${v}`,
      name: COMBINED_NAME + VARIANT_SUFFIX[v],
      when: VARIANT_WHEN[v],
    })
  }
  return out
}

export const MILESTONE_TEMPLATES: readonly MilestoneTemplate[] = buildMilestoneTemplates()

/**
 * The ones still to write — **which is not the same as the ones that do not
 * exist.**
 *
 * Judged on written copy, not on a row being present. A milestone template that
 * exists with an empty body is worse than a missing one: `pickTemplate` only
 * asks whether the key exists, so `plan` drafts from it, `claim` writes the
 * occurrence, and the event is spent. Taking a bare key list here made the
 * panel go quiet the moment such a row appeared — reproducing, one level down,
 * the exact failure the panel exists to stop ("six templates exist" reading as
 * "the templates exist").
 *
 * So the caller passes what it has, and anything without both a subject and a
 * body still counts as missing.
 */
export const missingMilestoneTemplates = (
  existing: readonly { key: string; subject?: string | null; body?: string | null }[] | readonly string[],
): MilestoneTemplate[] => {
  const written = new Set<string>()
  for (const row of existing) {
    if (typeof row === 'string') {
      written.add(row)
      continue
    }
    if ((row.subject ?? '').trim() && (row.body ?? '').trim()) written.add(row.key)
  }
  return MILESTONE_TEMPLATES.filter((t) => !written.has(t.key))
}

/** Is this key one the milestone engine will read? */
export const isMilestoneKey = (key: string): boolean =>
  MILESTONE_TEMPLATES.some((t) => t.key === key)

// ──────────────────────────────────────────────────────────────── the plan

/** A row that already exists in `outreach_log`, as far as this file cares. */
export type LoggedEvent = {
  agent_id: string
  trigger: string
  occurrence: string
}

/** What the day's feed said about one agent. Every field is optional. */
export type FeedRow = {
  agent_id: string
  /** Present when the agent appeared in New Joiners. May carry a join date. */
  joined?: { date?: string | null } | null
  /** Present when the agent appeared in Departures. The card carries no date. */
  departed?: boolean
  /**
   * Present when this agent was found in the full active-roster card without
   * ever having appeared in New Joiners — set by `sync-metabase.ts` after it
   * diffs that card against the roster, never derived here. Like `departed`,
   * the card carries no date; the occurrence is the day we first saw them.
   */
  possibleMissedJoiner?: boolean
  /** The dates the full-list cards are showing right now. */
  date_became_capped?: string | null
  date_became_elite?: string | null
  first_transaction_closed_at?: string | null
}

/** The agent as this file needs it — a subset of `Agent`, so tests need not build one. */
export type MilestoneAgent = {
  rezen_id: string
  birthday_month?: number | null
  birthday_day?: number | null
  anniversary_month?: number | null
  anniversary_day?: number | null
  /**
   * The year they joined Real, when Metabase has reported it — card 2368
   * only, filled in by `sync-metabase.ts`'s `planBackfill` a little at a time,
   * never overwritten once set. Used only to decide whether the "with years"
   * anniversary template is offered; see `ANNIVERSARY_YEARS_KEY`.
   */
  anniversary_year?: number | null
  /**
   * Lifetime closed-transaction count, straight off the roster (`agents.
   * closed_transactions`, already synced and already a merge field). Used
   * only to decide which anniversary chain is offered — the standard one or
   * the low-production one; see `LOW_PRODUCTION_MAX_CLOSED_TRANSACTIONS` and
   * `anniversaryCandidates`. Missing on purpose counts as low production —
   * Chris, 2026-09-20: "If the number of closed transactions is missing
   * treat them as below the threshold." An unconfirmed number should never
   * default an agent into the standard, congratulate-the-volume copy.
   */
  closed_transactions?: number | null
  /**
   * The day this agent's cap year rolls over, `YYYY-MM-DD`. **The cycle
   * anchor** — see `cycleAnchor`.
   */
  cap_reset_date?: string | null
  /**
   * The day this agent entered OUR roster — `agents.first_seen_at`. Not the day
   * they joined Real. **The line between their history and their future**, for
   * capping, Elite and first closing. See `predatesUs`.
   */
  first_seen_at?: string | null
  date_became_capped?: string | null
  date_became_elite?: string | null
  first_transaction_closed_at?: string | null
  is_active?: boolean
}

/**
 * Does this agent still satisfy what this trigger requires?
 *
 * Asked as a question about the trigger rather than written as "unless it is a
 * departure", which is the shape `claude/milestone-trigger-logic.md` insists on:
 * a bypass bolted onto a guard gets inherited by the next trigger somebody adds.
 *
 * Everything except a departure requires an agent who is still here. Without
 * this, an agent who left in March gets a birthday card in September — and, far
 * worse, the log row is consumed, so the year is spent even if they come back.
 *
 * **A departure is not checked against `is_active`, and that is deliberate.**
 * The evidence that somebody has left is the Departures card, not the column —
 * and depending on where the daily read ends up running, the column may not
 * have been flipped yet when this is called. Requiring `is_active === false`
 * here would make every departure depend on an ordering nothing guarantees.
 * `recheckAtHandoff` is where that question belongs, at the last moment before
 * the email opens.
 *
 * `is_active` absent is treated as present-and-true, so a caller passing a
 * partial record does not silently lose every draft.
 */
export function triggerFits(trigger: Trigger, agent: { is_active?: boolean | null }): boolean {
  if (isFarewell(trigger)) return true
  return agent.is_active !== false
}

/**
 * Is this the one trigger that WANTS somebody who has gone?
 *
 * Its own function, and exported, because the comment above says
 * `recheckAtHandoff` is where the same question belongs at send time — and for
 * a while that was aspirational. `recheckAtHandoff` blocked on `!is_active`
 * with no exception at all, so every farewell this system has ever drafted was
 * unsendable: queued, shown to Renee, and refused at the click with "has left
 * the brokerage since this was queued", which is the entire point of the
 * email.
 *
 * Written as a shared predicate rather than a second `=== 'departed'` in the
 * other file, on the same reasoning `triggerFits` itself was written on: two
 * places deciding the same thing will disagree eventually, and the disagreement
 * is a milestone email to the wrong person.
 *
 * Takes a `string`, not a `Trigger`. The caller at send time holds a draft row
 * out of the database, where the column is text — and widening `triggerFits`
 * to match would have thrown away the engine's own type safety to suit it.
 *
 * TRIMMED AND LOWERCASED, for the same reason the parameter is loose in the
 * first place. A bare `=== 'departed'` against a `text` column was the one
 * hazard the loose type invited, and every other text comparison in this
 * project already normalises — `suppressedFor` here, `suppressedBy` in
 * `emailrules.ts`, `rezen_id` throughout the sync. It fails closed either way,
 * which is exactly why it would have gone unnoticed.
 */
export function isFarewell(trigger: string | null | undefined): boolean {
  return String(trigger ?? '').trim().toLowerCase() === 'departed'
}

/**
 * The triggers that can only ever happen once to a person.
 *
 * A first closing is a first closing. When Metabase corrects the date — a data
 * fix, or a timestamp that renders either side of midnight — the ordinary
 * "the card disagrees with what we hold" rule reads the correction as a second
 * first closing and sends a second congratulations.
 *
 * Capping and reaching Elite are NOT on this list: both reset annually, so a
 * date in a new cycle is a real event. `isNewCycle` below is what tells a new
 * cycle from a correction, by asking which anniversary year each date falls in.
 */
export const ONCE_EVER: readonly Trigger[] = ['first_closing']

/**
 * THE FALLBACK ONLY. `isNewCycle` below is the rule.
 *
 * How far a cap or Elite date must move before the change counts as a new
 * cycle rather than a correction, for an agent whose anniversary we do not
 * hold. It is a guess, and it is used only because there is nothing better to
 * ask when the anniversary is blank — which is the normal state of a roster
 * still being filled in.
 *
 * The history, because it is the argument for not using a day count at all.
 * Three hundred was tried first and is wrong: a growing agent caps EARLIER each
 * year, so 273 days on is an ordinary second cap that a 300-day threshold files
 * as a correction and sends nothing about. Chris, 2026-08-20, on how fast two
 * milestones can follow each other: *"no telling how fast or long."* He is
 * right, and the consequence is that NO fixed number works — two real caps can
 * be weeks apart if one lands late in an anniversary year and the next lands
 * early in the following one. That is why the primary rule asks which
 * anniversary year each date falls in instead.
 *
 * Kept at 180 for the no-anniversary case, where the failure it risks is a
 * missed congratulation rather than a duplicate one.
 */
export const NEW_CYCLE_DAYS = 180

/**
 * The floor under a new cycle, in days, whatever the anniversary says.
 *
 * The anniversary rule below has one degenerate case and this is it. An agent
 * whose cap date sits within a few days of their own anniversary can have a
 * one-day correction land on the far side of the boundary — held 28 February,
 * card 2 March, anniversary 1 March — and a pure year comparison reads that
 * two-day nudge as a second cap and congratulates them twice.
 *
 * Fourteen days is comfortably wider than any plausible correction and far
 * narrower than any plausible pair of real caps: capping means paying a full
 * annual commission cap, so doing it twice inside a fortnight is not a thing
 * that happens. The cost of being wrong is one missed congratulation in a case
 * that has probably never occurred; the cost of not having it is a duplicate
 * "you capped!" to somebody who did not.
 *
 * **A judgement. Flagged to Chris with the rule itself.**
 */
export const MIN_CYCLE_GAP_DAYS = 14

/**
 * Did this happen before we started watching this agent?
 *
 * **`firstRun` is a property of the RUN; milestones are a property of the
 * AGENT, and the two come apart.** An agent who enters the roster after the
 * baseline run — the weekly reZEN sync, a re-import, the cutover seed — arrives
 * carrying a cap date, an Elite date and a first closing that may be years old.
 * Nothing had recorded them, so the ordinary "the card disagrees with what we
 * hold" rule read every one as new. Measured in review: **381 drafts,
 * congratulating people on closings from 2024.**
 *
 * It is not a hypothetical ordering either. `claude/cutover-monday.md` runs the
 * morning Metabase task FIRST, then the final seed, then imports the old
 * system's history — so on C-day the baseline is claimed against a roster
 * missing people, and the seed then adds the ones whose history drafts the next
 * morning.
 *
 * Chris, 2026-08-20, asked what should happen to milestones an agent reached
 * before we started watching them: **nothing.** Which is the standing decision
 * — *"we are not interested in the past"* — applied per agent instead of per
 * run.
 *
 * `first_seen_at` is the line, and it was already in the schema. An agent with
 * none is treated as always-known, because the alternative is silencing
 * everybody the day this ships.
 *
 * **Only the three date-comparison triggers use this.** A birthday is not a
 * thing that happened before we met somebody; it comes round. Anchoring
 * birthdays here would silence every agent's first birthday with us.
 */
export function predatesUs(agent: MilestoneAgent, occurrence: string): boolean {
  const seen = dayOf(agent.first_seen_at)
  if (!seen || !isDateString(occurrence)) return false
  return epochDay(occurrence) < epochDay(seen)
}

/**
 * Which anniversary-to-anniversary year a date falls in.
 *
 * Chris, 2026-08-20: *"This is not based on a calendar year, capping and
 * reaching Elite is based on your anniversary year which could be march to
 * march."* So the cycle for an agent with a 1 March anniversary runs
 * 1 March → 28 February, and the number returned is the year that period
 * STARTED in: both 2026-03-01 and 2027-02-28 return 2026.
 *
 * Returns `null` when the agent's anniversary is missing or unreadable, which
 * is the normal case for a roster still being filled in — the caller falls
 * back to `NEW_CYCLE_DAYS` rather than guessing a boundary.
 *
 * **The comparison is on the month and day as a pair, NOT on a resolved
 * boundary date, and that is a correctness fix rather than a style choice.**
 * Resolving the boundary through `occurrenceOn` clamps 29 February to the 28th
 * in a common year, which makes the boundary oscillate — and a 29 February
 * anniversary then produced one 366-DAY cycle in every four. Held 2027-02-28
 * and a card reading 2028-02-28 are 365 days apart and are two real caps, and
 * the clamped version filed them both in "the year beginning 2027" and drafted
 * nothing at all. Silent, and once every four years. Comparing (2,28) against
 * (2,29) directly gives every cycle exactly one year, leap or not.
 *
 * `occurrenceOn` is still what says whether the anniversary is READABLE,
 * probed against a leap year so that 29 February passes.
 */
export function anniversaryCycle(
  date: string,
  month: number | null | undefined,
  day: number | null | undefined,
): number | null {
  /**
   * Belt and braces, and said so plainly: `occurrenceOn` rejects anything that
   * is not an integer, so a month or day arriving as `'3'` — which the roster
   * really does do, per the same hazard the birthday side guards against —
   * already ends up returning `null` one line further down.
   *
   * Kept because it states the contract where a reader looks for it, and
   * because it is the only line that would still be right if `occurrenceOn`
   * ever grew a coercion. **Do not write a test for it — it cannot go red.**
   */
  if (typeof month !== 'number' || typeof day !== 'number') return null
  if (!isDateString(date)) return null
  /**
   * Readable, asked against a fixed year so that whether an anniversary is
   * USABLE never depends on which year we happen to be asking in.
   *
   * The year makes no difference and mutation says so: `occurrenceOn` clamps
   * 29 February to the 28th in a common year rather than rejecting it, so 2001
   * probes identically to 2000. Same finding as the birthday probe two hundred
   * lines below, recorded here too because "use a leap year" looks like a
   * load-bearing detail and is not.
   */
  if (occurrenceOn(2000, month, day) === null) return null
  const m = DATE_RE.exec(date)!
  const year = Number(m[1])
  const beforeAnniversary =
    Number(m[2]) < month || (Number(m[2]) === month && Number(m[3]) < day)
  return beforeAnniversary ? year - 1 : year
}

/**
 * Which month and day a cap year rolls over on, for one agent.
 *
 * **`cap_reset_date` first, and it is not a close call.** Measured on the live
 * roster 2026-08-20: 785 of 789 active agents have one, every single value is
 * the first of a month, and Metabase reports it **on the same card row as the
 * cap date it is being compared against**. Only 167 have a work anniversary, so
 * anchoring on the anniversary sent six hundred agents to the day-count
 * fallback for no reason.
 *
 * It also corrects the boundary. The rule was built against the anniversary
 * DAY; the reset is the **first of the anniversary month**. Chris's "could be
 * march to march" is 1 March, not the 14th — so a cap date in the first
 * fortnight of March used to land on the wrong side of the line.
 *
 * The work anniversary stays as the backup for the handful with no reset date,
 * and `NEW_CYCLE_DAYS` behind that. Returns null when neither is readable.
 */
export function cycleAnchor(agent: {
  cap_reset_date?: string | null
  anniversary_month?: number | null
  anniversary_day?: number | null
}): { month: number; day: number } | null {
  const reset = dayOf(agent.cap_reset_date)
  if (reset) {
    const m = DATE_RE.exec(reset)!
    return { month: Number(m[2]), day: Number(m[3]) }
  }
  if (
    typeof agent.anniversary_month === 'number' &&
    typeof agent.anniversary_day === 'number' &&
    occurrenceOn(2000, agent.anniversary_month, agent.anniversary_day) !== null
  ) {
    return { month: agent.anniversary_month, day: agent.anniversary_day }
  }
  return null
}

/**
 * Is this changed cap or Elite date a NEW milestone, or Metabase correcting
 * itself?
 *
 * The question the day count could not answer. A cap year runs from the
 * agent's own anniversary, so two real caps can be as little as a few weeks
 * apart — late in one anniversary year, early in the next — and any fixed
 * threshold wide enough to absorb a correction is also wide enough to swallow
 * that. Asking which anniversary year each date falls in removes the guess:
 * one cap per anniversary year is what capping MEANS, so a later year is a
 * later cap however few days separate them.
 *
 * Five ways it says no:
 *   - either date is unreadable, which fails closed rather than throwing;
 *   - the date moved backwards, which no new cycle ever does;
 *   - the date did not move at all;
 *   - both dates sit in the same anniversary year;
 *   - the dates straddle the boundary but are closer together than
 *     `MIN_CYCLE_GAP_DAYS`, which is a correction wearing a boundary.
 *
 * With no usable anniversary it falls back to `NEW_CYCLE_DAYS`, unchanged.
 *
 * `basis` says which of the two rules answered. **Nothing reads it yet** — it
 * is returned so the decision is assertable, and the tests lean on it heavily,
 * because "declined" on its own is also what the old day count produced.
 */
export function isNewCycle(
  held: string,
  onCard: string,
  agent: {
    cap_reset_date?: string | null
    anniversary_month?: number | null
    anniversary_day?: number | null
  },
): { newCycle: boolean; basis: 'anniversary' | 'days'; detail: string } {
  const anchor = cycleAnchor(agent)
  const basis: 'anniversary' | 'days' = anchor ? 'anniversary' : 'days'
  /**
   * FAILS CLOSED ON ANYTHING THAT IS NOT A DATE, and this is the first thing
   * it does.
   *
   * Both values arrive from `dayOf` inside `plan`, so neither can be unreadable
   * there today — but this is exported as "the rule", `anniversaryCycle` below
   * guards its own input, and `daysBetween` THROWS rather than returning
   * anything. A throw here does not decline one agent, it kills the day's run
   * for all of them.
   */
  if (!isDateString(held) || !isDateString(onCard)) {
    return {
      newCycle: false,
      basis,
      detail: 'one of the two dates is not readable, so this is not treated as a new cycle.',
    }
  }
  const moved = daysBetween(held, onCard)
  /**
   * Forwards only, and checked before anything else.
   *
   * `Math.abs` on the old day count made a card date more than a cycle EARLIER
   * look like a new cycle, drafting a congratulation dated in the past. The
   * anniversary rule cannot reproduce that — an earlier date cannot be in a
   * later year — but the guard is explicit rather than emergent, because the
   * next person to edit this should not have to derive it.
   */
  if (moved < 0) {
    return { newCycle: false, basis, detail: 'the date moved backwards, so it is a correction.' }
  }
  /** Unreachable from `plan`, which returns early on an unchanged date. */
  if (moved === 0) {
    return { newCycle: false, basis, detail: 'the date has not moved at all.' }
  }
  const heldCycle = anniversaryCycle(held, anchor?.month, anchor?.day)
  const cardCycle = anniversaryCycle(onCard, anchor?.month, anchor?.day)
  if (heldCycle === null || cardCycle === null) {
    /**
     * Blank and unreadable are BOTH the fallback, and they are reported
     * differently on purpose. "No anniversary on record" was said for both, and
     * it is a lie about the second one — the roster really does hand this file
     * a month as `'3'`, and an operator reading the declines was being told the
     * agent had no anniversary when what they had was a broken one, which is
     * the difference between "wait for the backfill" and "go and fix a row".
     */
    const why =
      agent.cap_reset_date == null &&
      agent.anniversary_month == null &&
      agent.anniversary_day == null
        ? 'no cap reset date and no anniversary on record'
        : 'the cap reset date and anniversary on record cannot be read'
    return moved >= NEW_CYCLE_DAYS
      ? {
          newCycle: true,
          basis: 'days',
          detail: `${why}, and the date moved ${moved} days, which is past the ${NEW_CYCLE_DAYS}-day fallback.`,
        }
      : {
          newCycle: false,
          basis: 'days',
          detail: `${why}, and the date moved only ${moved} days, inside the ${NEW_CYCLE_DAYS}-day fallback — treating it as a correction.`,
        }
  }
  if (cardCycle <= heldCycle) {
    return {
      newCycle: false,
      basis: 'anniversary',
      detail: `both dates fall in the anniversary year beginning ${heldCycle} — treating it as a correction.`,
    }
  }
  if (moved < MIN_CYCLE_GAP_DAYS) {
    return {
      newCycle: false,
      basis: 'anniversary',
      detail: `the dates straddle the anniversary but are only ${moved} days apart, which is a correction rather than a second cycle.`,
    }
  }
  return {
    newCycle: true,
    basis: 'anniversary',
    detail: `the anniversary year moved from ${heldCycle} to ${cardCycle}.`,
  }
}

/**
 * Two records of the same appearance trigger, closer together than this, are
 * one event seen twice.
 *
 * Both ninety-day cards — New Joiners and Departures — can show the same person
 * on ninety consecutive runs, and can report them first without a date and
 * later with one. Two occurrences inside that window cannot be separate
 * appearances, because the card would have been showing both. Beyond it, a
 * genuine leaver-and-rejoiner is welcomed again, which is the right answer.
 *
 * A hundred and twenty: the card's ninety, plus slack for a sync that has not
 * run in a while.
 *
 * **This replaced a flag that deduped on the trigger alone**, which was
 * stricter and wrong in one direction (somebody who rejoined two years later
 * was never welcomed again) and too weak in another (a joining reported
 * dateless on Monday and dated on Tuesday was two different keys, so two
 * welcome campaigns — on the one trigger of the seven that has multiple
 * touches).
 */
export const SAME_APPEARANCE_DAYS = 120

export type PlannedDraft = {
  agent_id: string
  /** The trigger the draft is filed under. `birthday_anniversary` for a merged pair. */
  trigger: string
  occurrence: string
  template_key: string
  variant: Variant | null
  /** Why this exists, in words a reviewer can read. Goes in `outreach_drafts.why`. */
  why: string
}

export type PlannedLog = {
  agent_id: string
  trigger: Trigger
  occurrence: string
  status: 'baseline' | 'drafted'
}

export type PlannedEnrolment = {
  agent_id: string
  occurrence: string
}

export type Declined = {
  agent_id: string
  trigger: Trigger
  occurrence: string
  /** One of a small closed set, so a screen can group them. */
  reason:
    | 'suppressed'
    | 'no_template'
    | 'already_logged'
    | 'inactive'
    | 'baseline'
    | 'bad_data'
    | 'not_new'
    | 'trigger_disabled'
  detail: string
}

export type Plan = {
  drafts: PlannedDraft[]
  log: PlannedLog[]
  enrol: PlannedEnrolment[]
  declined: Declined[]
}

export type PlanInput = {
  today: string
  agents: readonly MilestoneAgent[]
  /** The day's feed, keyed by agent. Absent for a run that only fires the calendar. */
  feed?: readonly FeedRow[]
  /** Every row already in `outreach_log`, for the agents in play. */
  logged: readonly LoggedEvent[]
  /** Tags per agent id. Missing means none. */
  tags?: Readonly<Record<string, readonly string[]>>
  /**
   * The keys in `email_templates` **that can actually be rendered** — a subject
   * and a body with something in them. Guidance keys may be included; they are
   * ignored.
   *
   * **AN EMPTY TEMPLATE MUST NOT BE IN THIS LIST, and that is not a tidiness
   * rule.** `pickTemplate` only asks whether a key exists. Hand it the key of a
   * template nobody has written yet and this drafts from it, `claim` writes the
   * occurrence to `outreach_log`, and the event is spent: a blank subject and a
   * blank body in Daily Outreach, a card she cannot send because the Outbox
   * refuses an empty message, and a birthday that is never detected again.
   *
   * Contrast `no_template`, which declines and deliberately does NOT claim, so
   * the day the copy is written every event already seen can still fire. That
   * is the behaviour an unwritten template is supposed to get, and it only
   * happens if the caller leaves the key out.
   *
   * Written here because the caller does not exist yet and this is the moment
   * to say it. The Templates screen refuses to create a milestone-keyed
   * template empty by either route, for the same reason — but a row can still
   * be emptied by editing it afterwards, so **the caller must filter; it cannot
   * assume the screen did.**
   */
  templates: readonly string[]
  /**
   * The first run of the system. Everything detected is recorded and nothing is
   * drafted — see `firstRun` below, which is more subtle than it looks.
   */
  firstRun?: boolean
  belatedWindowDays?: number
  /**
   * Which of the seven triggers are turned on. Missing entirely, or missing
   * a given trigger's key, means enabled — every existing caller that never
   * heard of this still behaves exactly as before.
   *
   * A trigger set to `false` here is declined with `trigger_disabled` and,
   * like a suppressed one, still CLAIMED as baseline — the reasoning is the
   * same one `suppressed` already relies on: an event that fires while the
   * trigger is off must not sit there waiting to draft the moment somebody
   * flips it back on, possibly for a birthday three weeks gone. Turning a
   * trigger off is "stop watching this", not "pause and catch up later".
   */
  enabledTriggers?: Readonly<Partial<Record<Trigger, boolean>>>
}

/**
 * What today should produce.
 *
 * ── ON `firstRun`, AND THE TRAP INSIDE IT ───────────────────────────────────
 *
 * The first run records everything it sees as `baseline`: recorded so nothing
 * is drafted for it a second time, and nobody is emailed for a birthday that
 * happened before the system existed.
 *
 * The trap is the obvious implementation of that sentence — "this agent has no
 * log rows, so this must be history" — which is wrong and stays wrong forever.
 * A birthday surfacing from the sixty-day look-back card in month seven is a
 * *new fact*, not old history, and `milestone-trigger-logic.md` says so
 * explicitly. Under the agent-shaped reading it would be silently baselined and
 * that person would never be written to.
 *
 * So `firstRun` is a property of THE RUN, passed in by the caller, and nothing
 * in here infers it from an agent's history. The two readings look identical
 * from the outside and differ by one silent email per person.
 */
export function plan(input: PlanInput): Plan {
  const {
    today,
    agents,
    feed = [],
    logged,
    tags = {},
    templates,
    firstRun = false,
    belatedWindowDays = BELATED_WINDOW_DAYS,
    enabledTriggers = {},
  } = input

  const out: Plan = { drafts: [], log: [], enrol: [], declined: [] }
  if (!isDateString(today)) return out

  const seen = new Set(logged.map((r) => `${r.agent_id}\u0000${r.trigger}\u0000${r.occurrence}`))
  const everSeen = new Map<string, string[]>()
  for (const r of logged) {
    const k = `${r.agent_id}\u0000${r.trigger}`
    const list = everSeen.get(k)
    if (list) list.push(r.occurrence)
    else everSeen.set(k, [r.occurrence])
  }

  /**
   * MERGED, NOT REPLACED.
   *
   * The feed is assembled from five separate Metabase cards, and the obvious
   * way for a sync to emit it is one row per card-row. Keying a Map on the
   * agent and calling `set` then throws away everything but the last card an
   * agent appeared on — so the agent who capped and reached Elite in the same
   * week loses one of the two, with nothing in `declined` to notice it by.
   *
   * That is the worst failure this module has: a real event producing no draft
   * and no trace. Later rows win field by field, so a caller that does hand
   * over one combined row per agent behaves exactly as before.
   */
  const feedFor = new Map<string, FeedRow>()
  for (const row of feed) {
    if (!row || typeof row.agent_id !== 'string' || !row.agent_id) continue
    const already = feedFor.get(row.agent_id)
    if (!already) {
      feedFor.set(row.agent_id, {
        agent_id: row.agent_id,
        joined: row.joined ?? undefined,
        departed: row.departed === true ? true : undefined,
        date_became_capped: row.date_became_capped ?? undefined,
        date_became_elite: row.date_became_elite ?? undefined,
        first_transaction_closed_at: row.first_transaction_closed_at ?? undefined,
        possibleMissedJoiner: row.possibleMissedJoiner === true ? true : undefined,
      })
      continue
    }
    /**
     * FILL A BLANK, NEVER BLANK A FILL — the standing rule the design doc
     * states for syncs, applied here because the same thing goes wrong.
     *
     * A generic field-wise copy is not enough, and the first attempt at this
     * was one. `Object.entries` never yields an absent key, so a
     * `v !== undefined` guard is close to dead — and what it fails to stop is
     * `null` and `false`, which are exactly what a normalising sync emits for
     * "this card had nothing for them". A later row saying
     * `{ capped: null, elite: '…' }` then erased the capped event the earlier
     * row carried, with nothing in `declined` to notice it by: the same silence
     * the merge was introduced to remove, moved one line down.
     *
     * Written out field by field rather than looped, so that a key of
     * `__proto__` in a machine-generated row cannot reach an object setter and
     * inject an event through the prototype chain.
     */
    if (row.joined) {
      // A joining WITH a date beats one without: the dated form is what keys
      // the occurrence, and the dateless form falls back to the run date.
      if (!already.joined || (!dayOf(already.joined.date) && dayOf(row.joined.date))) {
        already.joined = row.joined
      }
    }
    if (row.departed === true) already.departed = true
    if (row.possibleMissedJoiner === true) already.possibleMissedJoiner = true
    if (row.date_became_capped != null) already.date_became_capped = row.date_became_capped
    if (row.date_became_elite != null) already.date_became_elite = row.date_became_elite
    if (row.first_transaction_closed_at != null) {
      already.first_transaction_closed_at = row.first_transaction_closed_at
    }
  }

  /**
   * Defensive on purpose. `tags` arrives from a query keyed on agent ids, and
   * an id of `constructor` or `toString` answers something off
   * `Object.prototype` — a function, not an array. A bare `tags[id] ?? []`
   * hands that to `suppressedFor`, which calls `.map` on it, and the whole
   * day's plan is lost rather than one draft.
   *
   * `Array.isArray` is what actually does the work here; an own-property check
   * in front of it would be belt and braces with nothing left to hold up. Said
   * plainly so nobody adds one and writes a test that cannot fail.
   */
  const tagsFor = (id: string): readonly string[] => {
    const held = (tags as Record<string, unknown>)[id]
    return Array.isArray(held) ? held.filter((t) => typeof t === 'string') : []
  }

  /**
   * Absent or anything other than literal `false` reads as enabled — the same
   * "missing means on" default `PlanInput.enabledTriggers` documents, kept as
   * one place so a stray truthy-but-wrong value (a string `"false"` from a
   * hand-edited settings row, say) cannot read as disabled by accident.
   */
  const triggerEnabled = (t: Trigger): boolean => enabledTriggers[t] !== false

  /** Same reasoning: one null in `email_templates.key` must not lose the run. */
  const templateKeys = (templates ?? []).filter((k): k is string => typeof k === 'string')

  /**
   * Record one detected event. Everything funnels through here so that the
   * dedupe check, the suppression check and the template check happen in the
   * same order for all seven triggers — the alternative is seven copies of the
   * order, and six of them eventually being subtly different.
   */
  const consider = (opts: {
    agent: MilestoneAgent
    trigger: Trigger
    occurrence: string
    variant: Variant | null
    why: string
    /** Overrides the template candidates. Used by the merged birthday+anniversary pair. */
    candidates?: string[]
    /** The trigger the DRAFT is filed under, when it differs from the logged one. */
    draftTrigger?: string
    /**
     * Further triggers that this ONE draft settles.
     *
     * The merged birthday-and-work-anniversary email is one draft and two log
     * rows, so that next year each fires on its own again and a re-run today
     * finds both handled. An earlier version did this with a `logOnly` flag on
     * a second `consider()` call, which had a hole in it: when the birthday was
     * already logged and the anniversary was not, the second call logged the
     * anniversary as drafted and produced no draft at all. One email, silently
     * lost, in the case where the two halves disagree — which is exactly the
     * case the pairing exists to handle.
     */
    alsoLog?: readonly Trigger[]
    /**
     * Enrol into the welcome campaign instead of drafting a single email. The
     * one trigger of the seven that is not one draft — decided 2026-08-17:
     * *"This should be the only Milestone email with multiple touches."* The
     * drip machinery owns everything after this point, which is why no template
     * is looked up.
     */
    enrol?: boolean
    /**
     * Two occurrences of this trigger closer together than this many days are
     * the same event seen twice. Used by the joiner, where one appearance in a
     * ninety-day card can be reported first without a date and later with one.
     */
    sameEventWithinDays?: number
  }): void => {
    const { trigger, occurrence, variant, why } = opts
    const agent_id = opts.agent.rezen_id
    const key = `${agent_id}\u0000${trigger}\u0000${occurrence}`
    const settles: Trigger[] = [trigger, ...(opts.alsoLog ?? [])]

    /**
     * Declining fans out across every trigger this call was going to settle.
     *
     * A merged birthday-and-anniversary pair is one `consider()` call, and a
     * bare `declined.push({ trigger })` recorded only the birthday — so an
     * inactive agent's anniversary disappeared with no trace at all, on the one
     * path built to make silence impossible.
     */
    const decline = (reason: Declined['reason'], detail: string) => {
      for (const t of settles) out.declined.push({ agent_id, trigger: t, occurrence, reason, detail })
    }

    /**
     * Claim and record every trigger this draft settles, in one place. Claiming
     * before pushing is what stops a list containing the same agent twice from
     * producing two drafts.
     */
    const claim = (status: 'baseline' | 'drafted') => {
      for (const t of settles) {
        seen.add(`${agent_id}\u0000${t}\u0000${occurrence}`)
        /**
         * `everSeen` is updated as well as `seen`, and this half cannot change
         * a draft today — the exact-key `seen` check catches every in-run
         * repeat before the occurrence lists are consulted. It is kept because
         * the two structures describe the same ledger and one of them being
         * stale is the kind of thing that becomes wrong later. Stated so nobody
         * writes a test for it and wonders why it will not go red.
         */
        const k = `${agent_id}\u0000${t}`
        const list = everSeen.get(k)
        if (list) list.push(occurrence)
        else everSeen.set(k, [occurrence])
        out.log.push({ agent_id, trigger: t, occurrence, status })
      }
    }

    if (seen.has(key)) {
      decline('already_logged', 'This event has already been recorded.')
      return
    }

    /**
     * THE ONE THAT TURNS ONE EMAIL INTO NINETY, and its quieter sibling.
     *
     * An agent sits in a ninety-day card for up to ninety runs. Keying the
     * occurrence on the run date would make every run a fresh event — that is
     * the loud version. The quiet version is the same appearance reported two
     * different ways: dateless on Monday, keyed on Monday; dated on Tuesday,
     * keyed on the join date. Two keys, two welcome campaigns — on the one
     * trigger of the seven that has multiple touches.
     *
     * So a prior record counts as THIS SAME appearance when it is close to the
     * new occurrence **or** close to today. Both halves are needed:
     *
     *   - occurrence-to-occurrence catches two runs a day apart;
     *   - occurrence-to-today catches the `possible_missed_joiner` shape, where
     *     a joiner surfaces dateless on Monday and comes back on Tuesday
     *     carrying a join date eight months old. Those two occurrences are 250
     *     days apart and are unmistakably one person joining once.
     */
    const priors = everSeen.get(`${agent_id}\u0000${trigger}`) ?? []
    if (opts.sameEventWithinDays != null) {
      const window = opts.sameEventWithinDays
      const near = priors.find((o) => {
        /**
         * A prior we cannot read is treated as A MATCH, not as absent.
         *
         * `outreach_log.occurrence` is `text` and identifies an instance, not a
         * date — this very module writes `''` on a `bad_data` decline. Skipping
         * what cannot be parsed fails OPEN: one malformed legacy row and that
         * agent is enrolled in the welcome drip a second time, every run,
         * forever. One missed welcome can be put right by hand; a duplicated
         * drip sequence to a brand-new agent cannot.
         */
        if (!isDateString(o)) return true
        return (
          Math.abs(daysBetween(o, occurrence)) <= window ||
          Math.abs(daysBetween(o, today)) <= window
        )
      })
      if (near != null) {
        decline(
          'already_logged',
          isDateString(near)
            ? `Already recorded on ${near}, which is the same appearance inside a ${window}-day window.`
            : 'A previous record for this trigger has no readable date, so this is assumed to be the same appearance.',
        )
        return
      }
    }

    /**
     * Does the agent still satisfy what this trigger requires? Before the
     * baseline and suppression branches, because an agent who left in March is
     * not somebody whose September birthday should be recorded as handled —
     * they are not a candidate, and nothing about them should be written.
     */
    if (!triggerFits(trigger, opts.agent)) {
      decline('inactive', 'They have left the brokerage.')
      return
    }

    /**
     * Turned off wins over everything below it, including `firstRun` — an off
     * trigger has nothing to baseline-record differently depending on when it
     * ran, it is simply not watching. Checked against every trigger this call
     * SETTLES, not just the one it was invoked for: the merged birthday-and-
     * anniversary draft is claimed under `settles`, and if only one half's
     * check ran here the other could be silently drafted off the back of it.
     * The caller-side merge guard below keeps this from ever actually seeing
     * a mixed `settles` in practice, but the check stays here, not only there,
     * so this function is correct on its own and not merely correct today
     * because of how its one caller happens to behave.
     */
    if (settles.some((t) => !triggerEnabled(t))) {
      claim('baseline')
      decline('trigger_disabled', 'This milestone trigger is currently turned off.')
      return
    }

    /**
     * THE ORDER OF THE NEXT THREE BRANCHES IS LOAD-BEARING, and one version of
     * this had it exactly wrong.
     *
     * The template check was briefly moved above `firstRun`, reasoning that a
     * departure with no copy should not be baselined and then stay silent
     * forever once the copy lands. That is right for an ORDINARY run and wrong
     * for the first one, and the change opened a far worse hole: if the
     * templates query came back empty or partial on the single day the first
     * run happens — entirely plausible while the copy set is being written up
     * from six pieces to fourteen — nothing is baselined at all, and the NEXT
     * run drafts the whole historical backlog. A belated birthday for everyone
     * inside the sixty-day look-back window, plus every cap, Elite and
     * first-closing difference across 785 agents.
     *
     * So `firstRun` wins: an event that predates the system is history, which
     * is what the design doc asks for. `no_template` keeps its no-claim
     * behaviour on every run after the first, which is where it matters.
     */
    if (firstRun) {
      claim('baseline')
      decline('baseline', 'First run — recorded so it is never drafted, but nobody is emailed.')
      return
    }

    /**
     * …and the same answer for an agent who is new to US rather than the system
     * being new. Same claim, same silence, different reason so the two are
     * distinguishable in `declined`. See `predatesUs`.
     */
    if (SHAPE[trigger] === 'new_date' && predatesUs(opts.agent, occurrence)) {
      claim('baseline')
      decline(
        'baseline',
        `${LABEL[trigger]} on ${occurrence} predates ${dayOf(opts.agent.first_seen_at)}, when this agent first appeared here — recorded, nobody emailed.`,
      )
      return
    }

    /**
     * Suppression before the template check, so that an agent who has opted out
     * is filed as opted out. The other way round put people who were never
     * going to be emailed into the "missing copy" queue, where somebody would
     * eventually resolve them by writing copy.
     */
    const held = settles
      .map((t) => suppressedFor(t, tagsFor(agent_id)))
      .find((h) => h !== null)
    if (held) {
      /**
       * Recorded as well as declined. A suppressed birthday that is not logged
       * is re-detected tomorrow, and the day the tag comes off — which may be
       * three weeks later — it drafts, for a birthday three weeks gone.
       */
      claim('baseline')
      decline('suppressed', `Tagged "${held}".`)
      return
    }

    if (opts.enrol) {
      claim('drafted')
      out.enrol.push({ agent_id, occurrence })
      return
    }

    const candidates = opts.candidates ?? templateCandidates(trigger, variant)
    const template_key = pickTemplate(candidates, templateKeys)
    if (!template_key) {
      /**
       * NOT claimed and NOT logged. A missing template is a gap in the copy,
       * not an event that has been handled — logging it would mean that the day
       * the `departed` copy is finally written, every departure already
       * detected stays silent forever.
       */
      decline('no_template', `No template for ${candidates.join(' or ')}.`)
      return
    }

    claim('drafted')
    out.drafts.push({
      agent_id,
      trigger: opts.draftTrigger ?? trigger,
      occurrence,
      template_key,
      variant,
      why,
    })
  }
  for (const agent of agents) {
    const id = agent.rezen_id
    if (!id) continue
    const row = feedFor.get(id)

    // ── the calendar half ──────────────────────────────────────────────────
    /**
     * A month/day pair that is present but unusable is REPORTED, not skipped.
     *
     * `plan`'s whole premise is that `declined` explains everything it did not
     * do, and this was the one class of input that produced total silence: a
     * column that came back as text (`"9"` rather than `9`), or a day of 31 in
     * a 30-day month. The agent simply never got a birthday card and nothing
     * anywhere said why — which, against a doc insisting that a blank birthday
     * means *"not filled in yet"*, is the last thing anybody would investigate.
     */
    for (const [kind, m, d] of [
      ['birthday', agent.birthday_month, agent.birthday_day],
      ['anniversary', agent.anniversary_month, agent.anniversary_day],
    ] as const) {
      const partial = (m == null) !== (d == null)
      /**
       * Probed against a fixed year rather than this one, so that whether a
       * birthday is READABLE never depends on which year we are asking in.
       *
       * The year chosen makes no difference — `occurrenceOn` clamps 29 February
       * to the 28th in a common year, so a leap-day birthday is usable either
       * way. Checked by mutation: swapping 2000 for 2027 changes nothing. Worth
       * saying, because "use a leap year here" looks like a load-bearing detail
       * and is not.
       */
      const unusable =
        m != null && d != null && occurrenceOn(2000, Number(m), Number(d)) === null
      const notNumbers =
        (m != null && typeof m !== 'number') || (d != null && typeof d !== 'number')
      if (partial || unusable || notNumbers) {
        out.declined.push({
          agent_id: id,
          trigger: kind,
          occurrence: '',
          reason: 'bad_data',
          detail: `Cannot read a ${kind} from month ${JSON.stringify(m)} and day ${JSON.stringify(d)}.`,
        })
      }
    }

    /**
     * See `NEW_AGENT_BELATED_WINDOW_DAYS`. `seenAt` normalises `first_seen_at`
     * the same way `predatesUs` does, since it may be a full timestamp. No
     * `first_seen_at` (a legacy record) leaves `tenureDays` null, which reads
     * as "not new" — the ordinary window applies, same fallback `predatesUs`
     * and the anniversary tenure floor both use.
     */
    const seenAt = dayOf(agent.first_seen_at ?? null)
    const tenureDays = seenAt ? daysBetween(seenAt, today) : null
    const isNewAgent = tenureDays !== null && tenureDays < NEW_AGENT_BELATED_WINDOW_DAYS
    const effectiveWindow = isNewAgent
      ? Math.min(belatedWindowDays, NEW_AGENT_BELATED_WINDOW_DAYS)
      : belatedWindowDays

    const birthday = annualDue(agent.birthday_month, agent.birthday_day, today, effectiveWindow)
    const anniversary = annualDue(
      agent.anniversary_month,
      agent.anniversary_day,
      today,
      effectiveWindow,
    )

    /**
     * Both on the same day is one email, not two — when there is a template for
     * it. The pair is filed under `birthday_anniversary`, and BOTH log rows are
     * still written, so next year each fires on its own again and a re-run
     * today finds them handled.
     */
    /**
     * Merging is refused unless BOTH halves are genuinely outstanding.
     *
     * If one of the two is already logged — last year's run handled the
     * anniversary, this year's has not reached the birthday — merging would
     * bounce off the dedupe check and take the other half down with it. Falling
     * back to two separate drafts in that case is not a degradation: it is the
     * only outcome in which the outstanding half is written at all.
     *
     * The same applies to the tags. `no-birthday-email` on its own must not
     * silence a work anniversary, so a pair where either half is suppressed is
     * handled separately and each half answers for itself.
     */
    const bothOutstanding =
      birthday != null &&
      anniversary != null &&
      birthday.occurrence === anniversary.occurrence &&
      !seen.has(`${id}\u0000birthday\u0000${birthday.occurrence}`) &&
      !seen.has(`${id}\u0000anniversary\u0000${anniversary.occurrence}`) &&
      suppressedFor('birthday', tagsFor(id)) === suppressedFor('anniversary', tagsFor(id)) &&
      // Same reasoning as the suppression-equality check just above: a
      // trigger-off toggle on only one half of the pair must not take the
      // other half down with it, so an uneven pair falls back to two
      // separate `consider()` calls, each answering only for itself.
      triggerEnabled('birthday') === triggerEnabled('anniversary')

    const merged =
      bothOutstanding &&
      birthday != null &&
      Boolean(pickTemplate(templateCandidates(COMBINED_KEY, birthday.variant), templateKeys))

    if (merged && birthday) {
      /**
       * One person, one day, two milestones — and without this, two emails.
       * One draft, filed under `birthday_anniversary`; two log rows, so each
       * trigger fires on its own again next year.
       */
      consider({
        agent,
        trigger: 'birthday',
        occurrence: birthday.occurrence,
        variant: birthday.variant,
        why: whyAnnual('Birthday', birthday, 'and work anniversary'),
        /**
         * The variant is carried into the merged key too. Hard-coding
         * `[COMBINED_KEY]` here switched the whole variant fallback off for
         * exactly the agents who have two milestones at once: the draft said
         * "belated" in its `why` line and used the on-the-day copy.
         */
        candidates: templateCandidates(COMBINED_KEY, birthday.variant),
        draftTrigger: COMBINED_KEY,
        alsoLog: ['anniversary'],
      })
    } else {
      if (birthday) {
        consider({
          agent,
          trigger: 'birthday',
          occurrence: birthday.occurrence,
          variant: birthday.variant,
          why: whyAnnual('Birthday', birthday, null),
        })
      }
      if (anniversary) {
        consider({
          agent,
          trigger: 'anniversary',
          occurrence: anniversary.occurrence,
          variant: anniversary.variant,
          why: whyAnnual('Work anniversary', anniversary, null),
          candidates: anniversaryCandidates(agent, anniversary.variant),
        })
      }
    }

    if (!row) continue

    // ── the feed half ──────────────────────────────────────────────────────

    /**
     * Joined. The occurrence is the join date Metabase reports, or the date we
     * first saw them — **never today's run date recomputed daily.** An agent
     * sits in the ninety-day New Joiners list for up to ninety runs, and that
     * one substitution is the difference between one welcome and ninety.
     *
     * With a reported join date the ordinary per-occurrence dedupe is exact,
     * and somebody who leaves and rejoins is welcomed twice, correctly. Without
     * one there is nothing to tell instances apart, so the rule falls back to
     * "have we ever welcomed this agent" — which costs a second welcome for a
     * rejoiner and saves eighty-nine for everybody else.
     */
    if (row.joined) {
      const reported = dayOf(row.joined.date)
      consider({
        agent,
        trigger: 'welcome',
        occurrence: reported ?? today,
        variant: null,
        why: reported
          ? `They joined on ${reported}.`
          : 'They appeared in New Joiners, which carries no join date.',
        // No reported date means nothing tells one joining from another, so the
        // dedupe falls back to "have we ever welcomed this agent".
        sameEventWithinDays: SAME_APPEARANCE_DAYS,
        enrol: true,
      })
    }

    /**
     * Departed. Same ninety-day shape, and the card carries no date at all — so
     * the occurrence is the day we first saw them in it, fixed at that moment.
     *
     * There is no `departed` template yet, so in practice every one of these
     * declines with `no_template` and stays undecided until the copy is written.
     * That is the intended behaviour: the alternative logs them as handled and
     * they are never drafted, even after the template arrives.
     */
    if (row.departed) {
      consider({
        agent,
        trigger: 'departed',
        occurrence: today,
        variant: null,
        why: 'They have left the brokerage. Renee sends a short survey asking why.',
        sameEventWithinDays: SAME_APPEARANCE_DAYS,
      })
    }

    /**
     * Newly SC-licensed, not new to Real. Same shape as `departed`: the active-
     * roster card carries no date of any kind, so the occurrence is fixed at
     * the day `sync-metabase.ts` first saw them there.
     *
     * NO `enrol`. `welcome`'s enrolment starts the multi-touch onboarding
     * sequence for somebody joining Real — wrong here, since this agent has
     * been with Real all along. This is one milestone email, like `capped` or
     * `first_closing`, not a campaign.
     */
    if (row.possibleMissedJoiner) {
      consider({
        agent,
        trigger: 'sc_licensed',
        occurrence: today,
        variant: null,
        why: 'They appeared in the full South Carolina roster without appearing in New Joiners first — already with Real, newly active here.',
        sameEventWithinDays: SAME_APPEARANCE_DAYS,
      })
    }

    // ── the three full-list cards ──────────────────────────────────────────
    /**
     * Driven from `NEW_DATE_FIELD`, not from a list written out again here.
     *
     * `SHAPE` is documentation and this does not pretend otherwise — an earlier
     * version filtered on it, which read as a mechanism and was not one, since
     * the two agree by construction. What keeps them honest is an assertion in
     * `test-milestones.mjs` that the `new_date` entries in `SHAPE` are exactly
     * the keys of `NEW_DATE_FIELD`. Add a fourth trigger to one and forget the
     * other, and the suite says so.
     */
    for (const trigger of NEW_DATE_TRIGGERS) {
      const field = NEW_DATE_FIELD[trigger]
      const raw = row[field]
      const onCard = dayOf(raw)
      if (!onCard) {
        /**
         * A card value that is present but unreadable is REPORTED. The
         * birthday side of this file grew a `bad_data` reason for exactly this
         * silence and the feed side was left without one — so a
         * `date_became_capped` of `'sometime'` produced no draft, no decline,
         * and nothing to investigate.
         */
        if (raw != null && raw !== '') {
          out.declined.push({
            agent_id: id,
            trigger,
            occurrence: '',
            reason: 'bad_data',
            detail: `The ${LABEL[trigger].toLowerCase()} card holds ${JSON.stringify(raw)}, which is not a date.`,
          })
        }
        continue
      }
      /**
       * WHAT WE ALREADY TOLD THEM BEATS WHAT THE COLUMN SAYS.
       *
       * The guards below used to consult `agent[field]` alone — the roster
       * column — and `plan()` never writes that column. So on the day after a
       * first closing was drafted, a one-day correction from Metabase found the
       * column still empty, saw no previous date, and sent a second
       * congratulations on a once-in-a-lifetime event. The `outreach_log` rows
       * are already in hand and are the authoritative answer to "have we
       * spoken to this person about this", so they are consulted first and the
       * column is the fallback.
       */
      /**
       * A PRIOR OCCURRENCE THAT IS NOT A DATE IS DROPPED HERE, AND THAT IS A
       * KNOWN HOLE — deliberately left, not overlooked. See
       * `claude/milestone-engine-log.md`.
       *
       * `outreach_log.occurrence` is `text`, and this module writes `''` into
       * it on its own bad-data decline. Such a row makes `held` null, which
       * skips the once-ever guard and drafts a second "congratulations on your
       * first closing".
       *
       * Failing closed here — declining the trigger — was written and REVERTED,
       * because it was locally right and globally wrong in two ways the review
       * caught: it sits above `consider()`, which is the only place `firstRun`
       * baselining happens, so an agent with one bad row is never baselined and
       * the run AFTER the row is fixed drafts their entire history; and, sitting
       * above the `held === onCard` continue, it locks that trigger out for that
       * agent forever with nothing to clear it. On `capped` and `elite`, which
       * reset annually, that costs every future year to buy nothing.
       *
       * **The fix has to be baseline-aware, and it is its own piece of work.**
       */
      const priors = everSeen.get(`${id}\u0000${trigger}`) ?? []
      const logged = priors.filter(isDateString)
      const lastTold = logged.length
        ? logged.reduce((a, b) => (epochDay(b) > epochDay(a) ? b : a))
        : null
      const held = lastTold ?? dayOf(agent[field])
      /**
       * "Newly" can only mean a comparison against the record: these three
       * cards are full lists, not "newly" feeds, so every capped agent appears
       * on every run. The event is the card disagreeing with what we hold —
       * including the common case of a column we were holding empty.
       */
      if (held === onCard) continue
      /**
       * BUT A CHANGED DATE IS NOT AUTOMATICALLY A NEW MILESTONE.
       *
       * A first closing happens once in a lifetime, so a later date for it is
       * Metabase correcting itself — and the naive rule sends a second
       * "congratulations on your first closing". Capping and Elite do reset
       * annually, so a date in a new cycle is real and a nudge of a day or two
       * is not.
       */
      if (held) {
        if (ONCE_EVER.includes(trigger)) {
          out.declined.push({
            agent_id: id,
            trigger,
            occurrence: onCard,
            reason: 'not_new',
            detail: `${LABEL[trigger]} can only happen once and ${held} is already recorded — treating ${onCard} as a correction.`,
          })
          continue
        }
        /**
         * `isNewCycle` owns this question now — anniversary year first, the
         * day count only when there is no anniversary to ask about. The
         * forwards-only guard moved in there with it; see the note on
         * `Math.abs` at its head for what that guard is protecting against.
         */
        const cycle = isNewCycle(held, onCard, agent)
        if (!cycle.newCycle) {
          out.declined.push({
            agent_id: id,
            trigger,
            occurrence: onCard,
            reason: 'not_new',
            detail: `${LABEL[trigger]} moved from ${held} to ${onCard} — ${cycle.detail}`,
          })
          continue
        }
      }
      consider({
        agent,
        trigger,
        occurrence: onCard,
        variant: null,
        why: held
          ? `${LABEL[trigger]} — a new cycle, ${onCard}, against ${held} before.`
          : `${LABEL[trigger]} on ${onCard}.`,
      })
    }
  }

  return out
}

/**
 * Which column each full-list card is compared against. Beside `SHAPE`'s
 * `new_date` entries rather than inline, so the two cannot drift apart.
 */
export const NEW_DATE_FIELD = {
  capped: 'date_became_capped',
  elite: 'date_became_elite',
  first_closing: 'first_transaction_closed_at',
} as const

type NewDateTrigger = keyof typeof NEW_DATE_FIELD

export const NEW_DATE_TRIGGERS = Object.keys(NEW_DATE_FIELD) as NewDateTrigger[]

/** What each trigger is called in a sentence a reviewer reads. */
export const LABEL: Record<Trigger, string> = {
  birthday: 'Birthday',
  anniversary: 'Work anniversary',
  capped: 'Reached cap',
  elite: 'Reached Elite',
  first_closing: 'First closing',
  welcome: 'Joined',
  departed: 'Departed',
  sc_licensed: 'Newly SC-licensed',
}

/** The sentence a reviewer reads above the draft. */
function whyAnnual(label: string, due: Annual, alsoToday: string | null): string {
  const also = alsoToday ? ` (${alsoToday} the same day)` : ''
  if (due.variant === 'on_day') return `${label} today${also}.`
  if (due.variant === 'early') {
    const when = due.daysAway === 1 ? 'tomorrow' : `in ${due.daysAway} days`
    return `${label} ${when}, at the weekend — sending before it${also}.`
  }
  const ago = -due.daysAway
  return `${label} was ${ago} day${ago === 1 ? '' : 's'} ago — belated${also}.`
}
